import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { tap } from 'rxjs';

import { AiCostCall } from '../../core/admin/ai-cost.types';
import {
  AiGuidanceNote,
  AiGuidanceSection,
  AiGuidanceService,
} from '../../core/admin/ai-guidance.service';
import { IconComponent } from '../icon/icon';
import { CONTEXT_DIALOG } from '../context-blocks-panel/context-dialog';
import {
  AiNoteDialogComponent,
  AiNoteDialogData,
  AiNotesListData,
  AiNotesListDialogComponent,
} from './ai-note-dialogs';

/**
 * Notes for AI on one file: the same row and pop-ups as a context block. Each
 * person sees only what they wrote here; approved notes work together for the
 * section and kind of evaluation, on the server.
 */
@Component({
  selector: 'app-ai-notes',
  imports: [DatePipe, IconComponent, MatCardModule],
  templateUrl: './ai-notes.html',
  styleUrl: './ai-notes.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiNotesComponent {
  private readonly service = inject(AiGuidanceService);
  private readonly dialog = inject(MatDialog);

  readonly section = input.required<AiGuidanceSection>();
  /** The file these notes are written on. */
  readonly sourceRef = input.required<string>();
  /** This file's AI calls, newest first. */
  readonly calls = input<AiCostCall[]>([]);

  protected readonly notes = signal<AiGuidanceNote[]>([]);
  protected readonly error = signal<string | null>(null);
  protected readonly latest = computed(() => this.notes()[0] ?? null);

  /** Real answers from the evaluations a note can improve: no stubs, no upload checks,
   * and no comparison runs, which no live prompt reads. */
  protected readonly responses = computed(() =>
    this.calls().filter(
      (call) =>
        call.output_tokens > 0 &&
        call.purpose !== 'DOC_CHECK' &&
        call.purpose !== 'MODEL_COMPARISON',
    ),
  );

  constructor() {
    effect(() => {
      const section = this.section();
      const ref = this.sourceRef();
      this.service.mine(section, ref).subscribe({
        next: (rows) => this.notes.set(rows),
        error: () => this.error.set('Your notes could not be loaded.'),
      });
    });
  }

  protected openAdd(): void {
    const data: AiNoteDialogData = {
      responses: this.responses(),
      save: (text, responseRef) =>
        this.service
          .propose({
            text,
            response_ref: responseRef,
            section: this.section(),
            source_ref: this.sourceRef(),
          })
          .pipe(tap((note) => this.notes.update((rows) => [note, ...rows]))),
    };
    this.dialog.open(AiNoteDialogComponent, { ...CONTEXT_DIALOG, data });
  }

  protected openList(): void {
    const data: AiNotesListData = {
      notes: this.notes,
      canAdd: this.responses().length > 0,
      add: () => this.openAdd(),
    };
    this.dialog.open(AiNotesListDialogComponent, { ...CONTEXT_DIALOG, data });
  }
}
